<template>
  <b-modal
    id="importTableViewModal"
    v-model="importTableViewModal.show"
    size="xl"
    :title="isReview ? `Import Status` : `Import Preview`"
    body-class="p-0"
    :hide-footer="hideFooter"
    :header-class="isReview ? 'review-modal-header' : ''"
    @hidden="onHideModal"
    @show="onShow"
  >
    <content-spinner
      :show="loading"
      :fullscreen="true"
    />
    <b-row v-if="!loading">
      <b-col cols="12">
        <div
          v-if="!isReview"
          class="d-flex justify-content-between p-3"
        >
          <div class="d-flex">
            <b-form-radio
              id="showAllRecords"
              v-model="selected"
              class=" mr-2"
              value="allRecord"
              @change="onChange"
            >
              Show All Records
            </b-form-radio>
            <b-form-radio
              id="showRecordsWithError"
              v-model="selected"
              class=" mr-2"
              value="errors"
              @change="onChange"
            >
              Show Errors
            </b-form-radio>
          </div>
        </div>
        <div class="p-2">
          <show-error
            :show="!!validationError.message"
            :text="validationError.message"
          />
          <div
            v-if="importTableViewModal.itemCountWithDecimalPlacesExceeded"
            role="alert"
            class="alert alert-warning"
          >
            {{ itemCountWithDecimalPlacesExceededMessage() }}
          </div>
        </div>
        <complex-table-view
          :small="true"
          :is-imported="isImported"
          :loading="tableLoading"
          :fields="tableFields"
          :items="localItems"
          :total-rows="localItems.length"
          :modal-name="'import'"
          :is-import-review="isReview"
          @sort-changed="onSortChanged"
          @on-add-new-relational-entity="onAddNewRelationalEntity"
          @filter="onFilter"
          @on-change-select="onSelectChange"
          @on-import-lookup-add="updateLookupOptions"
          @on-delete="onDelete"
        />
      </b-col>
    </b-row>
    <div
      slot="modal-footer"
      class="d-flex"
    >
      <b-button
        v-if="!isReview"
        id="importTableViewModalCancelBtn"
        class="mt-3 mr-3"
        block
        @click="$bvModal.hide('importTableViewModal')"
      >
        Cancel
      </b-button>
      <b-button
        id="importTableViewModalOkBtn"
        variant="primary"
        block
        class="mt-3"
        :disabled="disabled && !isReview"
        @click.prevent="isReview ? onHideModal() : onSubmit()"
      >
        {{ isReview ? `OK` : `Commit` }}
      </b-button>
    </div>
  </b-modal>
</template>

<script>
import {createNamespacedHelpers} from 'vuex';
import ContentSpinner from '../ContentSpinner';
import ComplexTable from './ComplexTable';
import ComplexTableView from './components/ComplexTableView';
import filterMixin from '../../mixins/filter.mixin';
import moment from 'moment';
import ShowError from '../ShowError';
import _ from 'lodash';

const {mapState, mapActions, mapGetters} = createNamespacedHelpers('dataManagement');
export default {
  name: 'ImportTableViewModal',
  components: {
    ComplexTableView,
    ComplexTable,
    ContentSpinner,
    ShowError,
  },
  mixins: [filterMixin],
  data() {
    return {
      items: [],
      reviewItems: [],
      isImported: false,
      selected: 'allRecord',
      isChanged: false,
      loading: false,
      tableLoading: false,
      isReview: false,
      importedData: {},
      filteredItems: [],
      filters: {},
      validationError: {
        message: null,
      },
    };
  },
  computed: {
    ...mapState({
      importTableViewModal: state => state.table.importTableViewModal,
      schema: state => state.table.schema,
    }),
    ...mapGetters(['fields']),
    ...mapState({
      permissionTypes: state => state.permissionTypes,
      pagination: state => state.table.pagination,
    }),
    tableFields() {
      return this.fields.filter(f => {
        if ([this.permissionTypes.none, this.permissionTypes.deny].includes(f.currentUserPermissionLevel)) {
          return false;
        }

        if (f.isCalculated) {
          return false;
        }
        let show = f.displayInTable && !f.isPrimaryKey;

        if (!this.readOnly) {
          show = show && !f.isInternal;
        }
        if(f.isRLSField){
          show = false;
        }

        return show;
      });
    },
    localItems() {
      return this.isReview ? this.items : this.items.filter(item => item.isVisible);
    },
    disabled() {
      if (this.validationError.message) {
        return true;
      }
      if (!this.localItems.length) {
        return true;
      }
      return this.localItems.filter(li => (
        li.options && Object.keys(li.options).length > 0)
          || li._rowVariant
          || li.errors && Object.keys(li.errors).length,
      ).length > 0;
    },
    hideFooter() {
      return this.selected === 'errors' && !this.isReview;
    },
  },
  methods: {
    ...mapActions([
      'hideImportTableViewModal',
      'addNewRecord',
      'showAddNewRecordModal',
      'deleteImportRow',
      'updateImportTableViewModalItems',
      'updateImportReview',
      'updateImportLookupOptions',
      'updateTableData',
    ]),
    onHideModal() {
      this.isImported = false;
      this.isReview = false;
      this.importedData = {};
      this.selected = 'allRecord';
      this.filteredItems = [];
      this.filters = {};
      this.hideImportTableViewModal();
    },
    async validateItems() {
      this.loading = true;

      const params = {
        data: this.getValidItems(),
        tableId: this.$route.params.tableId,
        validateOnly: true,
        modalName: 'import',
      };

      const {success, body} = await this.addNewRecord(params);
      if (success) {
        body.resultSetTemp.forEach(result => {
          if (result.uploadStatus === 'rejected' && Array.isArray(result.validationErrors)) {
            const validationRuleErrorIndex = result.validationErrors.findIndex(error => error.indexOf('Failed validation rule') > -1);
            const postStatusValidationErrorIndex = result.validationErrors.findIndex(error => error.indexOf('The same user can not post the record.') > -1);
            const businnessKeyValidationErrorIndex = result.validationErrors.findIndex(error => error.indexOf('Duplicate business key') > -1);

            const index = result.recordNumber;
            if (validationRuleErrorIndex > -1) {
              this.items[index]._rowVariant = 'danger';
              this.items[index].validationErrorMessage = result.validationErrors[validationRuleErrorIndex];
            } else if (businnessKeyValidationErrorIndex > -1) {
              this.items[index]._rowVariant = 'danger';
              this.items[index].validationErrorMessage = result.validationErrors[businnessKeyValidationErrorIndex];
            }

            if (postStatusValidationErrorIndex > -1) {
              this.items[index]._cellVariants = {
                ...this.items[index]._cellVariants ? this.items[index]._cellVariants : {},
                PostedStatus: 'danger',
              };
              this.items[index].errors = {
                ...this.items[index].errors ? this.items[index].errors : {},
                PostedStatus: result.validationErrors[postStatusValidationErrorIndex],
              };
            }
          }
        });
      }

      this.items = [...this.items];
      this.loading = false;
    },
    async onShow() {
      this.loading = true;
      this.isImported = true;
      this.items = [...this.importTableViewModal.items];

      await this.validateItems();
      this.loading = false; 
    },
    onChange(e) {
      this.tableLoading = true;
      this.changeItems(e);
      setTimeout(() => {
        this.tableLoading = false;
      });
    },
    onDelete(index) {
      this.deleteImportRow(index);
      this.afterItemChange();
    },
    onSelectChange(item, index) {
      this.updateImportTableViewModalItems({item, index});
      this.afterItemChange();
    },
    updateLookupOptions(field, option) {
      this.updateImportLookupOptions({field, option});
      this.afterItemChange();
    },
    afterItemChange() {
      const isFiltered = Object.keys(this.filters).length;
      if (isFiltered) {
        this.onFilter(this.filters);
      } else {
        this.onChange(this.selected);
      }
    },
    changeItems(e, isErrorFilter = false) {
      const isFiltered = Object.keys(this.filters).length;
      if (this.selected !== e || !isFiltered || isErrorFilter) {
        const items = isFiltered ? this.filteredItems : [...this.importTableViewModal.items];
        this.items = e === 'allRecord'
          ? items
          : items.filter(li => (
            li.options && Object.keys(li.options).length > 0)
                || li._rowVariant === 'danger'
                || li.errors && Object.keys(li.errors).length,
          );
      }
    },
    getValidItems() {
      const validItems = [];
      JSON.parse(JSON.stringify(this.importTableViewModal.items)).forEach(item => {
        if (item.isVisible) {
          this.fields.forEach(field => {
            const dataTypeJSON = field.dataTypeJSON
              ? JSON.parse(field.dataTypeJSON)
              : {};
            if (field.dataTypeName === this.fieldValues.lookup && item[field.name]) {
              const lookupFieldMap = this.importTableViewModal.fieldDataMap.get(field.name);
              const lookupId = lookupFieldMap.get(
                item[field.name]
                  .toString()
                  .toLowerCase()
                  .trim()
                  .replace(/\s\s+/g, ' '),
              );
              if (lookupId) {
                item[field.name] = lookupId.id;
              }
            }
            if ([this.fieldValues.dateTime, this.fieldValues.date].includes(field.dataTypeName)) {
              const dateFormat = JSON.parse(field.dataTypeJSON).FormatString;
              item[field.name] = item[field.name]
                ? moment(item[field.name], `${dateFormat} HH:mm:ss`).format('YYYY/MM/DDTHH:mm:ss')
                : null;
            }
            if (field.dataTypeName === this.fieldValues.boolean) {
              item[field.name] = ['true', '1', true, 1].includes(typeof item[field.name] === 'string' ? item[field.name].toLowerCase() : item[field.name]);
            }
            if (field.dataTypeName === this.fieldValues.text && item[field.name]) {
              const textFieldValue = item[field.name].toString().trim();
              item[field.name] = dataTypeJSON.IsLineBreak
                ? textFieldValue
                : textFieldValue.replace(/\s\s+/g, ' ');
            }

            if (field.dataTypeName === this.fieldValues.parentChildHierarchy && item[field.name]) {
              const hierarchy = field.data.options.hierarchyField;
              const hierarchyGuid = this.findHierarchyOption(field, item[field.name]);

              if (hierarchyGuid) {
                item[field.name] = hierarchyGuid[hierarchy.primary.name];
              }
            }

            if (field.isInternal && field.dataTypeName !== 'primaryKey') {
              delete item[field.name];
            }
            if (item.options) {
              delete item.options;
            }
            if (item._cellVariants) {
              delete item._cellVariants;
            }
          });
          validItems.push(item);
        }
      });

      return validItems;
    },
    async onSubmit() {
      this.loading = true;

      const validItems = this.getValidItems();

      const params = {
        data: validItems,
        tableId: this.$route.params.tableId,
        modalName: 'import',
      };
      const {success, body} = await this.addNewRecord(params);
      await this.updateTableData({
        tableId: this.$route.params.tableId,
        pagination: this.pagination,
      });
      this.loading = false;
      if (success) {
        this.isReview = true;
        const isFiltered = Object.keys(this.filters).length;
        if (isFiltered) {
          this.filteredItems = [];
          this.filters = {};
          this.onFilter(this.filters);
        }

        const resData = body.resultSetTemp;
        if (resData.length > 0) {

          const rejectedFields = resData.filter(d => d.uploadStatus === 'rejected');
          if (rejectedFields.length) {
            this.importedData.rejected = [...rejectedFields];
          }

          if (body.recordsRejected === validItems.length) {
            this.$toasted.error(
              'Unable to import data',
              {duration: TOASTER_DURATION},
            );
          } else {
            this.$toasted.success(
              `Updated: ${body.recordsUpdated},
              Not Updated: ${body.recordsIgnored},
              Imported: ${body.recordsInserted},
              Rejected: ${body.recordsRejected}  row(s)`,
              {duration: TOASTER_DURATION},
            );

            body.resultSetTemp = [...body.resultSetTemp.filter(d => d['uploadStatus'] !== 'rejected')];
            const params = {
              inline: false,
              insertData: body,
            };
            this.$emit('changed-row', params);
          }
        } else {
          this.importedData.notUpdated = validItems.map((item, index) => ({recordNumber: index}));
          this.$toasted.success(
            `Not Updated: ${body.recordsIgnored} row(s)`,
            {duration: TOASTER_DURATION},
          );
        }
        this.getReviewItems(this.items);
        // Default show error page after import data
        this.selected = 'errors';
        this.onChange('errors');
      } else {
        this.onHideModal();
        this.$toasted.error('Unable to import data', {duration: TOASTER_DURATION});
      }
    },
    itemCountWithDecimalPlacesExceededMessage() {
      return `${this.importTableViewModal.itemCountWithDecimalPlacesExceeded} records contain a value where the number of decimals has exceeded the allowed amount and have been trimmed to the appropriate number of decimal places allowed as per the field set up.`;
    },
    getReviewItems(items) {
      const reviewItems = [];
      items.forEach((item, index) => {
        if (item.isVisible) {
          delete item._cellVariants;
          let result = _.cloneDeep(item);
          const errors = [];
          if (this.importedData.rejected) {
            const rejectedFields = this.importedData.rejected;
            rejectedFields.forEach(f => {
              if (f.recordNumber === index) {
                result = {...result, _rowVariant: 'danger'};
                errors.push(f.validationErrors.map(e => e).join(', '));
              }
            });
          }
          if (this.selected === 'allRecord') {
            if (this.importedData.updated) {
              const updatedFields = this.importedData.updated;
              updatedFields.forEach(f => {
                if (f.recordNumber === index) {
                  result = {...result, _rowVariant: 'info'};
                }
              });
            }
            if (this.importedData.imported) {
              const importedFields = this.importedData.imported;
              importedFields.forEach(f => {
                if (f.recordNumber === index) {
                  result = {...result, _rowVariant: 'success'};
                }
              });
            }
            if (this.importedData.notUpdated) {
              const notUpdatedFields = this.importedData.notUpdated;
              notUpdatedFields.forEach(f => {
                if (f.recordNumber === index) {
                  result = {...result, _rowVariant: 'warning'};
                }
              });
            }
          }
          if (errors.length > 0) {
            result.errors = [...errors];
          }
          reviewItems.push(result);
        }
      });
      this.updateImportReview(reviewItems);
      this.items = [...this.importTableViewModal.items];
    },
    buildTree(arr, hierarchyField) {
      const levelField = hierarchyField.data.options.hierarchyField.level;
      const primaryField = hierarchyField.data.options.hierarchyField.primary;
      const parentField = hierarchyField.data.options.hierarchyField.parent;

      const jsonData = JSON.parse(hierarchyField.dataTypeJSON);
      const minLevel = jsonData.MinimumLevelSelectable;
      const maxLevel = jsonData.MaximumLevelSelectable;

      const tree = [];
      const treeLevels = {};
      const rawArray = JSON.parse(JSON.stringify(arr)); //make copy of original
      const map = {};

      for (const item of rawArray) {
        map[item[primaryField.name]] = item;
      }

      for (const item of rawArray) {
        if (item[parentField.name]) {
          if (!map[item[parentField.name]].children) {
            map[item[parentField.name]].children = [];
          }
          map[item[parentField.name]].children = [...map[item[parentField.name]].children, item];
        } else {
          tree.push(map[item[primaryField.name]]);
        }
      }
      this.setLevelName(tree, 0, levelField, treeLevels, minLevel, maxLevel);

      return {tree, treeLevels};
    },
    setLevelName(arr, level, levelField, treeLevels, minLevel, maxLevel) {
      if (!arr) {
        return;
      }

      let disabled = false;
      if (minLevel > level) {
        if (maxLevel === null) {
          disabled = true;
        }
        if (maxLevel >= level) {
          disabled = true;
        }
      }
      if (maxLevel !== null && !(minLevel === level || maxLevel >= level)) {
        disabled = true;
      }

      arr.forEach((elem) => {
        treeLevels[level] = elem[levelField.name];
        elem.level = level;
        elem.disabled = disabled;
        this.setLevelName(elem.children, level + 1, levelField, treeLevels, minLevel, maxLevel);
      });
    },
    findHierarchyOption(field, value) {
      const hierarchy = field.data.options.hierarchyField;
      field = {...field, ...this.buildTree(field.data.options.items, field)};

      const iterator = item => {
        if (!item.disabled && item[hierarchy.display.name] === value) {
          result = item;
          return true;
        }
        return Array.isArray(item.children) && item.children.some(iterator);
      };

      let result;
      field.tree.some(iterator);
      return result;
    },
    onSortChanged(e) {
      this.items = this.localItems.sort(function (a, b) {
        if (a[e.sortBy] < b[e.sortBy]) {
          return e.sortDesc ? 1 : -1;
        }
        if (a[e.sortBy] > b[e.sortBy]) {
          return e.sortDesc ? -1 : 1;
        }
        return 0;
      });
    },
    onFilter(filters) {
      const filter = filters.filter || filters;
      this.filters = filter;
      this.items = this.importTableViewModal.items.filter((item) => {
        for (const filterFieldName in filter) {
          if (!filter.hasOwnProperty(filterFieldName)) {
            continue;
          }
          const field = this.tableFields.find((f) => f.name === filterFieldName);
          if (
            field.dataTypeName === this.fieldValues.text
              && !this.textFilter(filter[filterFieldName], item[filterFieldName])
          ) {
            return false;
          } else if ([
            this.fieldValues.wholeNumber,
            this.fieldValues.decimalNumber,
            this.fieldValues.floatNumber,
          ].includes(field.dataTypeName)
              && !this.numberFilter(filter[filterFieldName], item[filterFieldName])
          ) {
            return false;
          } else if (
            field.dataTypeName === this.fieldValues.boolean
              && !this.booleanFilter(filter[filterFieldName], item[filterFieldName])
          ) {
            return false;
          } else if (
            field.dataTypeName === this.fieldValues.lookup
              && !this.lookupFilter(filter[filterFieldName], item[filterFieldName])
          ) {
            return false;
          } else if (
            field.dataTypeName === this.fieldValues.parentChildHierarchy
              && !this.hierarchyFilter(filter[filterFieldName], item[filterFieldName], field)
          ) {
            return false;
          } else if (
            field.dataTypeName === this.fieldValues.dateTime
              && !this.dateFilter(filter[filterFieldName], item[filterFieldName])
          ) {
            return false;
          }
        }
        return true;
      });
      this.filteredItems = _.cloneDeep(this.items);
      if (this.selected === 'errors') {
        this.changeItems(this.selected, true);
      } else {
        this.onChange(this.selected);
      }
    },
    hierarchyFilter(filteredField, uploadField, field) {
      return filteredField.selectItems.findIndex((elem) => {
        const hierarchyField = field.data.options.hierarchyField;
        const elemId = this.isReview ? elem[hierarchyField.primary.name] : elem[hierarchyField.display.name];
        return elemId === uploadField;
      }) > -1;
    },
    lookupFilter(filteredField, uploadField) {
      return filteredField.selectItems.findIndex((elem) => {
        return elem.id === uploadField;
      }) > -1;
    },
    levenshtein(optionsValue, selectedValue) {
      // eslint-disable-next-line prefer-const
      let i, j, cost, matchedWords = [];

      if (optionsValue.length === 0) {
        return selectedValue.length;
      }
      if (selectedValue.length === 0) {
        return optionsValue.length;
      }

      for (i = 0; i <= optionsValue.length; i++) {
        matchedWords[i] = [];
        matchedWords[i][0] = i;
      }

      for (j = 0; j <= selectedValue.length; j++) {
        matchedWords[0][j] = j;
      }

      for (i = 1; i <= optionsValue.length; i++) {
        for (j = 1; j <= selectedValue.length; j++) {
          cost = optionsValue.charAt(i - 1) === selectedValue.charAt(j - 1) ? 0 : 1;
          matchedWords[i][j] = Math.min(matchedWords[i - 1][j] + 1, matchedWords[i][j - 1] + 1, matchedWords[i - 1][j - 1] + cost);

          if (i > 1 && j > 1 && optionsValue.charAt(i - 1) === selectedValue.charAt(j - 2) && optionsValue.charAt(i - 2) === selectedValue.charAt(j - 1)) {
            matchedWords[i][j] = Math.min(matchedWords[i][j], matchedWords[i - 2][j - 2] + cost);
          }
        }
      }

      return {name: optionsValue, value: matchedWords[optionsValue.length][selectedValue.length]};
    },
    async onAddNewRelationalEntity(item, index, field) {
      const primary = this.fields.find((f) => f.isPrimaryKey);   
      const params = {
        inline: true,
        record: {},
        relationalTableId: field.relationalTableId,
        rowIndex: index,
        defaultLookupValue: this.getLookupDefaultValue(item, field, true),
        field: field,
        recordUuid: item[primary.name],
        entityUuid: this.$route.params.tableId,
      };     
      this.showAddNewRecordModal(params);
    },
    getLookupDefaultValue(item, field, isRelational = false) {
      if (field !== undefined) {
        const suggestionName = item[`${field.name}__$LookupStatus`];
        let lookupDefaultValue;
        const relationalField = field.data.lookup;
        if (item.customClass && this.isValidSuggestions(suggestionName)) {
          const suggestions = suggestionName.Suggestions || suggestionName.suggestions;
          lookupDefaultValue = suggestions[0].OriginalValue || suggestions[0].originalValue;
        }
        return isRelational ? {[relationalField.value]: lookupDefaultValue} : {[field.name]: lookupDefaultValue};
      }
    },
    isValidSuggestions(suggestionName) {
      if (suggestionName) {
        const suggestions = suggestionName.Suggestions || suggestionName.suggestions;
        return suggestions && suggestions.length > 0;
      }
      return false;
    },
  },
};
</script>

<style lang="scss">
.rectangle {
  width: 10px;
  height: 10px;
}

.review-modal-header {
  border-bottom: unset !important;
}
</style>
